import {isLeft} from 'fp-ts/lib/Either'
import reporter from 'io-ts-reporters'
import React, {useEffect, useState} from 'react'
import {
  WebhookCredentialConfiguration,
  WebhookCredentialConfigurationCodec
} from '../../common/types'
import {sanitizeRecord} from '../../common/types/helpers'
import {BetaFlagMarker} from '../../components/feature-flags'
import {FormSpacer, HelpText} from '../../components/form'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../components/library/dropdown/content'
import {Select, TextInput} from '../../components/library/form'
import {PasswordInput} from '../../components/library/form/password-input'
import {Icon} from '../../components/library/icon/icon'
import {List, NoResultsPermissions} from '../../components/library/list'
import styled from 'styled-components'
import {Tag} from '../../components/library/tag/tag'
import {Switch} from '../../components/library/toggles/switch'
import {Toggle} from '../../components/library/toggles/toggle'
import {FormModal} from '../../components/modals'
import {OverflowFooter} from '../../components/overflow'
import {
  PermissionAction,
  PermissionResource,
  WebhookCredentialsOrderBy
} from '../../data/workbench-api/generated/types'
import {
  useAddWebhookCredentialMutation,
  useDeleteWebhookCredentialMutation,
  useUpdateWebhookCredentialMutation,
  useWebhookCredentialListLazyQuery,
  WebhookCredentialDetailFragment
} from '../../data/workbench-api/generated/webhook-credential'
import {EnvironmentNode, useEnvironment} from '../../environment'
import {useStateChanged} from '../../hooks/use-state-changed'
import {
  useAnalytics,
  useUserIdentifiedLink
} from '../../lib/analytics/analytics'
import {useToast} from '../../lib/toast'

interface WebhookCredentialListProps {
  environment: EnvironmentNode
  bleed?: string
  renderLayout?: (
    content: React.ReactElement,
    addNew: () => void
  ) => React.ReactElement
}

// new data shape, omits legacy columns, and requires configuration
type WebhookCredential = Omit<
  WebhookCredentialDetailFragment,
  'clientId' | 'clientSecret' | 'identityUrl' | 'expirySeconds'
> & {
  configuration: WebhookCredentialConfiguration
}

// migrate legacy columns into configuration object, and validate its contents
const migrateLegacyWebhookCredential = (
  old: WebhookCredentialDetailFragment
): WebhookCredential => {
  const {configuration} = old

  if (configuration) {
    const result = WebhookCredentialConfigurationCodec.decode(configuration)
    if (isLeft(result)) {
      // warn in the case of malformed data
      console.warn(reporter.report(result))
    }
  }

  if (old.configuration) {
    return {
      ...old,
      configuration: sanitizeRecord(
        configuration
      ) as WebhookCredentialConfiguration
    }
  }

  return {
    ...old,
    configuration: {
      authType: 'client-credentials',
      // casts are safe, as they should be set for legacy records
      clientId: old.clientId as string,
      clientSecret: old.clientSecret as string,
      identityUrl: old.identityUrl as string,
      expirySeconds: old.expirySeconds as number,
      scope: undefined,
      requestScheme: 'url-parameters'
    }
  }
}

export const WebhookCredentialList = ({
  environment,
  renderLayout = content => content,
  bleed
}: WebhookCredentialListProps) => {
  const toaster = useToast()
  const analytics = useAnalytics()
  const canView = environment.hasPermission(
    PermissionResource.WebhookCredential,
    PermissionAction.View
  )
  const canEdit = environment.hasPermission(
    PermissionResource.WebhookCredential,
    PermissionAction.Edit
  )
  const [query, {data, refetch = () => null, loading}] =
    useWebhookCredentialListLazyQuery({
      variables: {environmentId: environment.id}
    })

  useEffect(() => {
    if (canView) {
      query()

      analytics.track({
        event: 'Viewed webhook credentials configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>webhooks>webhook-credentials'
        }
      })
    }
  }, [analytics, canView, environment.id, environment.organisationId, query])

  const [showAddModal, setShowAddModal] = useState(false)
  const [editWebhookCredential, setEditWebhookCredential] =
    useState<WebhookCredential>()

  const webhookCredentials = data?.webhookCredentials?.nodes.map(
    migrateLegacyWebhookCredential
  )

  return renderLayout(
    <>
      <List<WebhookCredential, WebhookCredentialsOrderBy>
        columns={[
          {
            id: 'name',
            header: 'Name',
            accessor: r => (
              <a onClick={() => setEditWebhookCredential(r)}>{r.name}</a>
            )
          },
          {
            id: 'authType',
            header: 'Auth type',
            accessor: r =>
              r.configuration.authType === 'aws-v4'
                ? 'AWS signed request'
                : r.configuration.authType === 'basic'
                ? 'Basic auth'
                : 'Client credentials'
          },
          {
            id: 'webhookEndpoints',
            header: 'Used by',
            accessor: r => r.webhookEndpoints.totalCount
          },
          {
            id: 'enabled',
            header: 'Status',
            accessor: r => (
              <Tag
                appearance={r.webhookEndpoints.totalCount ? 'green' : 'light'}
              >
                {r.webhookEndpoints.totalCount ? 'Active' : 'Inactive'}
              </Tag>
            )
          }
        ]}
        renderOverflow={(webhookCredential, closeDropdown) => (
          <Overflow
            environment={environment}
            webhookCredential={webhookCredential}
            setEditWebhookCredential={setEditWebhookCredential}
            closeDropdown={() => {
              closeDropdown()
              refetch()
            }}
            canEdit={canEdit}
          />
        )}
        rows={webhookCredentials ?? []}
        loading={loading}
        bleed={bleed}
        renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
      />

      {showAddModal ? (
        <AddModal
          organisationId={environment.organisationId}
          environmentId={environment.id}
          handleClose={() => {
            setShowAddModal(false)
          }}
          onCompleted={() => {
            analytics.track({
              event: 'Added configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>webhooks>webhook-credentials'
              }
            })
            setShowAddModal(false)
            toaster('Credential added')
            refetch()
          }}
        />
      ) : null}
      {editWebhookCredential ? (
        <EditModal
          organisationId={environment.organisationId}
          webhookCredential={editWebhookCredential}
          handleClose={() => {
            setEditWebhookCredential(undefined)
          }}
          onCompleted={() => {
            analytics.track({
              event: 'Edited configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>webhooks>webhook-credentials'
              }
            })
            setEditWebhookCredential(undefined)
            toaster('Credential updated')
            refetch()
          }}
        />
      ) : null}
    </>,
    () => setShowAddModal(true)
  )
}

interface OverflowProps {
  webhookCredential: WebhookCredential
  setEditWebhookCredential: (obj: WebhookCredential) => void
  closeDropdown: () => void
  canEdit: boolean
  environment: EnvironmentNode
}

const Overflow = ({
  webhookCredential,
  setEditWebhookCredential,
  closeDropdown,
  canEdit,
  environment
}: OverflowProps) => {
  const toaster = useToast()
  const analytics = useAnalytics()

  const [deleteMutation] = useDeleteWebhookCredentialMutation({
    onCompleted: () => {
      closeDropdown()
      toaster('Credential deleted')
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>webhooks>webhook-credentials'
        }
      })
    }
  })
  return (
    <>
      <DropdownBody>
        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              setEditWebhookCredential(webhookCredential)
              closeDropdown()
            }}
          >
            Edit
          </a>
        </DropdownItem>

        {!webhookCredential.webhookEndpoints.totalCount ? (
          <DropdownItem disabled={!canEdit}>
            <a
              onClick={() => {
                if (confirm('Deletion is permanent. Are you sure?')) {
                  deleteMutation({
                    variables: {
                      id: webhookCredential.id,
                      environmentId: webhookCredential.environmentId
                    }
                  })
                }
              }}
            >
              Delete
            </a>
          </DropdownItem>
        ) : null}
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter
          id={webhookCredential.id}
          updated={webhookCredential.updated}
          created={webhookCredential.created}
        />
      </DropdownFooter>
    </>
  )
}

type WebhookCredentialFields = Pick<WebhookCredential, 'name' | 'configuration'>
interface WebhookCredentialFormProps {
  webhookCredential: WebhookCredentialFields
  update: (
    updater: (obj: WebhookCredentialFields) => WebhookCredentialFields
  ) => void
  organisationId: string
}
export const WebhookCredentialForm = ({
  webhookCredential,
  update,
  organisationId
}: WebhookCredentialFormProps) => {
  const {configuration} = webhookCredential
  const analytics = useAnalytics()
  const {environment} = useEnvironment()
  const addSubQueryParam = useUserIdentifiedLink()

  const docsLink = addSubQueryParam(
    'https://documentation.atomic.io/workbench/configuration#webhook-credentials'
  )

  return (
    <>
      <TextInput
        upLabel="Name"
        label="Enter a name for this credential..."
        value={webhookCredential.name || ''}
        onChange={name => update(c => ({...c, name}))}
      />
      <FormSpacer />

      <h4>
        Credential type <BetaFlagMarker />
      </h4>
      <Toggle
        style={{width: '100%'}}
        value={configuration.authType}
        onChange={authType =>
          update(c => ({
            ...c,
            configuration: {
              ...c.configuration,
              authType
            } as WebhookCredentialConfiguration
          }))
        }
        options={[
          {value: 'client-credentials', text: 'Client credentials'},
          {value: 'aws-v4', text: 'AWS signed request'},
          {value: 'basic', text: 'Basic auth'}
        ]}
        legacyMode={true}
      />
      <FormSpacer />

      {configuration.authType === 'aws-v4' ? (
        <>
          <TextInput
            upLabel="Access key id"
            label="Enter access key id..."
            value={configuration.accessKeyId ?? ''}
            onChange={accessKeyId =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, accessKeyId}
              }))
            }
          />
          <FormSpacer />
          <PasswordInput
            upLabel="Secret access key"
            label="Enter secret access key..."
            value={configuration.secretAccessKey ?? ''}
            onChange={secretAccessKey =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, secretAccessKey}
              }))
            }
          />
          <FormSpacer />
          <TextInput
            upLabel="Session token"
            label="Enter session token (optional)..."
            value={configuration.sessionToken ?? ''}
            onChange={sessionToken =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, sessionToken}
              }))
            }
          />
          <FormSpacer />
          <Switch
            label="Sign query"
            isForm={true}
            labelPosition="left"
            checked={configuration.signQuery ?? false}
            onChange={signQuery =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, signQuery}
              }))
            }
          />
        </>
      ) : configuration.authType === 'basic' ? (
        <>
          <Warning
            style={{marginBottom: '20px'}}
            message="This option is less secure, consider using the alternative options where possible."
          />
          <TextInput
            upLabel="Username"
            label="Enter username..."
            value={configuration.username ?? ''}
            onChange={username =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, username}
              }))
            }
          />
          <FormSpacer />
          <PasswordInput
            upLabel="Password"
            label="Enter password (optional)..."
            value={configuration.password ?? ''}
            onChange={password =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, password}
              }))
            }
          />
        </>
      ) : (
        <>
          <TextInput
            upLabel="Client ID"
            label="Enter client ID..."
            value={configuration.clientId ?? ''}
            onChange={clientId =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, clientId}
              }))
            }
          />
          <FormSpacer />
          <PasswordInput
            upLabel="Client secret"
            label="Enter client secret..."
            value={configuration.clientSecret ?? ''}
            onChange={clientSecret =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, clientSecret}
              }))
            }
          />
          <FormSpacer />
          <TextInput
            upLabel="Identity URL"
            label="Enter identity URL..."
            value={configuration.identityUrl ?? ''}
            onChange={identityUrl =>
              update(c => ({
                ...c,
                configuration: {...c.configuration, identityUrl}
              }))
            }
          />
          <FormSpacer />
          <TextInput
            upLabel="Scope"
            label="Enter scope..."
            value={configuration.scope ?? ''}
            onChange={scope =>
              update(c => ({
                ...c,
                configuration: {
                  ...c.configuration,
                  scope: scope || undefined
                }
              }))
            }
          />
          <FormSpacer />
          <Select
            options={[
              {
                value: 'url-parameters',
                text: 'URL query parameters (GET)'
              },
              {
                value: 'body-parameters',
                text: 'Body parameters (POST x-www-form-urlencoded)'
              }
            ]}
            label="Request scheme"
            upLabel="Request scheme"
            value={configuration.requestScheme ?? 'body-parameters'}
            firstOptionDisabled
            onChange={requestScheme =>
              update(c => ({
                ...c,
                configuration: {
                  ...c.configuration,
                  requestScheme: requestScheme ?? 'body-parameters'
                }
              }))
            }
          />
        </>
      )}
      <HelpText visible={true}>
        See the{' '}
        <a
          href={docsLink}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            if (environment) {
              analytics.track({
                event: 'Redirected to external URL',
                properties: {
                  url: docsLink,
                  environment_id: environment.id,
                  org_id: organisationId,
                  category: 'general'
                }
              })
            }
          }}
        >
          documentation
        </a>{' '}
        for configuration details.
      </HelpText>
    </>
  )
}

const credentialIsValid = ({name, configuration}: WebhookCredentialFields) =>
  !!name &&
  !!(
    (
      (configuration.authType === 'client-credentials' &&
        configuration.clientId &&
        configuration.clientSecret &&
        configuration.identityUrl) ||
      (configuration.authType === 'aws-v4' &&
        configuration.accessKeyId &&
        configuration.secretAccessKey) ||
      (configuration.authType === 'basic' && configuration.username)
    ) // for 'basic' password can be empty
  )

interface AddModalProps {
  organisationId: string
  environmentId: string
  handleClose(): void
  onCompleted: () => void
}
export const AddModal = ({
  organisationId,
  environmentId,
  handleClose,
  onCompleted
}: AddModalProps) => {
  const [localObj, setLocalObj, dirty] =
    useStateChanged<WebhookCredentialFields>({
      name: '',
      configuration: {
        authType: 'client-credentials',
        clientId: '',
        clientSecret: '',
        identityUrl: '',
        expirySeconds: 900,
        scope: undefined,
        requestScheme: 'body-parameters'
      }
    })
  const [doMutation, {loading, error}] = useAddWebhookCredentialMutation({
    onCompleted
  })

  return (
    <FormModal
      title="Add webhook credential"
      handleClose={handleClose}
      onSubmit={() =>
        doMutation({
          variables: {
            environmentId,
            ...localObj
          }
        })
      }
      error={error}
      isValid={dirty && !loading && credentialIsValid(localObj)}
    >
      <WebhookCredentialForm
        organisationId={organisationId}
        webhookCredential={localObj}
        update={setLocalObj}
      />
    </FormModal>
  )
}

interface EditModalProps {
  organisationId: string
  webhookCredential: WebhookCredential
  handleClose: () => void
  onCompleted: () => void
}

export const EditModal = ({
  organisationId,
  webhookCredential,
  handleClose,
  onCompleted
}: EditModalProps) => {
  const [localObj, setLocalObj, dirty] =
    useStateChanged<WebhookCredentialFields>({
      name: webhookCredential.name,
      configuration: webhookCredential.configuration
    })
  const [doMutation, {loading, error}] = useUpdateWebhookCredentialMutation({
    onCompleted
  })

  return (
    <FormModal
      title="Edit webhook credential"
      handleClose={handleClose}
      onSubmit={() => {
        return doMutation({
          variables: {
            environmentId: webhookCredential.environmentId,
            id: webhookCredential.id,
            ...localObj
          }
        })
      }}
      error={error}
      isValid={dirty && !loading && credentialIsValid(localObj)}
    >
      <WebhookCredentialForm
        organisationId={organisationId}
        webhookCredential={localObj}
        update={setLocalObj}
      />
    </FormModal>
  )
}

const WarnIconWrap = styled.div`
  color: ${p => p.theme.orange};
  padding-right: 10px;
  float: left;
`

const WarnMessageWrap = styled.div`
  font-size: ${p => p.theme.fontSizeS};
  line-height: ${p => p.theme.lineHeight};
`

export const Warning = ({
  message,
  style
}: {
  message: string
  style?: React.CSSProperties
}) => {
  return (
    <div style={style}>
      <WarnIconWrap>
        <Icon name="negative" />
      </WarnIconWrap>
      <WarnMessageWrap>{message}</WarnMessageWrap>
    </div>
  )
}
